import {dequal} from 'dequal'
import {klona} from 'klona'

import {
  FoodGrubhubCartItemWithoutQuantity,
  FoodGrubhubCartItemWithoutQuantityAndMoreInfo,
  type FoodGrubhubCartV1,
} from './foodGrubhubCartDb'

function findItemIndex(
  foodCartItems: FoodGrubhubCartV1['foodCartItems'],
  item: FoodGrubhubCartItemWithoutQuantity | FoodGrubhubCartItemWithoutQuantityAndMoreInfo,
) {
  // TODO: Cannot fallback empty values to default yet: like `[]` and `undefined` or not present
  const itemCleaned =
    'moreInfo' in item
      ? FoodGrubhubCartItemWithoutQuantity(item)
      : FoodGrubhubCartItemWithoutQuantityAndMoreInfo(item)
  // Check if the item is exist the cart via compare productId, and all the selectedOptions with quantity
  return foodCartItems.findIndex(i => {
    return (
      i.restaurantId === itemCleaned.restaurantId &&
      i.menuItemId === itemCleaned.menuItemId &&
      i.specialInstructions === itemCleaned.specialInstructions &&
      dequal(i.options, itemCleaned.options)
    )
  })
}

function clearFoodGrubhubCartIfStoreIdChanged(
  foodCart: FoodGrubhubCartV1,
  item: FoodGrubhubCartItemWithoutQuantity,
) {
  if (foodCart.foodCartItems.some(i => i.restaurantId !== item.restaurantId)) {
    foodCart.foodCartItems = []
  }
}

export function addToFoodGrubhubCart({
  foodGrubhubCart,
  item,
  quantity = 1,
}: {
  foodGrubhubCart: FoodGrubhubCartV1
  item: FoodGrubhubCartItemWithoutQuantity
  quantity?: number
}) {
  const newFoodGrubhubCart = klona(foodGrubhubCart)

  clearFoodGrubhubCartIfStoreIdChanged(newFoodGrubhubCart, item)

  const existItemIndex = findItemIndex(foodGrubhubCart.foodCartItems, item)

  if (existItemIndex !== -1) {
    invariant(newFoodGrubhubCart.foodCartItems[existItemIndex])
    newFoodGrubhubCart.foodCartItems[existItemIndex].quantity += quantity
  } else {
    newFoodGrubhubCart.foodCartItems.unshift({
      ...item,
      quantity,
    })
  }

  return newFoodGrubhubCart
}

export function removeFromFoodGrubhubCart({
  foodGrubhubCart,
  itemOrIndex,
  quantity,
}: {
  foodGrubhubCart: FoodGrubhubCartV1
  itemOrIndex: FoodGrubhubCartItemWithoutQuantityAndMoreInfo | number
  quantity?: number // if quantity is 0, <0 or missing, remove the item entirely
}) {
  const newFoodGrubhubCart = klona(foodGrubhubCart)

  const existItemIndex =
    typeof itemOrIndex === 'number'
      ? itemOrIndex
      : findItemIndex(foodGrubhubCart.foodCartItems, itemOrIndex)

  if (existItemIndex !== -1) {
    invariant(newFoodGrubhubCart.foodCartItems[existItemIndex])

    if (
      quantity &&
      quantity > 0 &&
      newFoodGrubhubCart.foodCartItems[existItemIndex].quantity > quantity
    ) {
      newFoodGrubhubCart.foodCartItems[existItemIndex].quantity -= quantity
    } else {
      newFoodGrubhubCart.foodCartItems.splice(existItemIndex, 1)
    }
  }

  return newFoodGrubhubCart
}

export function updateFoodGrubhubCartProductQuantity({
  foodGrubhubCart,
  itemOrIndex,
  quantity,
}: {
  foodGrubhubCart: FoodGrubhubCartV1
  itemOrIndex: FoodGrubhubCartItemWithoutQuantityAndMoreInfo | number
  quantity: number
}) {
  const newFoodGrubhubCart = klona(foodGrubhubCart)

  const existItemIndex =
    typeof itemOrIndex === 'number'
      ? itemOrIndex
      : findItemIndex(foodGrubhubCart.foodCartItems, itemOrIndex)

  if (existItemIndex !== -1) {
    if (quantity > 0) {
      if (newFoodGrubhubCart.foodCartItems[existItemIndex]) {
        newFoodGrubhubCart.foodCartItems[existItemIndex].quantity = quantity
      }
    } else {
      newFoodGrubhubCart.foodCartItems.splice(existItemIndex, 1)
    }
  }

  return newFoodGrubhubCart
}
